import {Injectable} from "@angular/core";
import {ContactRepo} from "../repositories/api/contact.repo";
import {map} from "rxjs/operators";
import {ContactDto} from "../repositories/dto/contact.dto";
import {SearchFilterRequest, SearchRequest} from "../repositories/dto/search/search-table.dto.req";
import {Observable} from "rxjs";
import {Additionals} from "../repositories/item/additionals";
import {AddressDto} from "../repositories/dto/address.dto";
import {AbstractService} from "./abstract.service";
import {WrapperDto} from "../repositories/dto/wrapper.dto";
import {SearchContactRes} from "../repositories/dto/search/search-contact.res";
import {PhoneDto} from "../repositories/dto/phone.dto";
import {BankAddressDto} from "../repositories/dto/bank-address.dto";
import {EmailDto} from "../repositories/dto/email.dto";
import {OccurrenceDto} from "../repositories/dto/occurrence.dto";
import {VoidDto} from "../repositories/dto/void.dto";
import {HyperlinkDto} from "../repositories/dto/hyperlink.dto";
import {isDefined, isUuid} from "../libs/utils/epic.utils";
import {ContactCategory} from "../libs/enum/contact-category.enum";
import {MediaDto} from "../repositories/dto/media.dto";
import {ContactType} from "../libs/enum/contact-type";

@Injectable({providedIn: 'root'})
export class ContactService extends AbstractService {

  private readonly contactRepo: ContactRepo = this.repoProvider.getRepo(ContactRepo);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getById(contactId: string, _add?: Additionals): Observable<ContactDto> {
    return this.contactRepo.getByIdCached(this.getOrg(), contactId)
      .pipe(map((w) => w.getFirstData()));
  }

  public update(contact: ContactDto): Observable<ContactDto> {
    this.clearCache(contact.id);
    this.clearCache(contact.contactId);
    return this.contactRepo.update(this.getOrg(), contact)
      .pipe(map((w) => w.getFirstData()));
  }

  public delete(contactId: string): Observable<VoidDto> {
    this.clearCache(contactId);
    return this.contactRepo.delete(this.getOrg(), contactId)
      .pipe(map((w) => w.getFirstData()));
  }

  public getAcademicDegrees(): Observable<string[]> {
    return this.getFilterValueByField('academicDegree');
  }

  public getSalutations(): Observable<string[]> {
    return this.getFilterValueByField('salutation');
  }

  public clearCache(contactId: string | undefined): void {
    if (isUuid(contactId)) {
      this.contactRepo.cacheRemove(this.getOrg(), contactId);
    }
  }

  public createPrintout(contactId: string): Observable<MediaDto> {
    return this.contactRepo.createPrintout(this.getOrg(), contactId)
      .pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // #### Address
  // ##########################################################################

  public getAddresses(contact: ContactDto): Observable<AddressDto[]> {
    return this.contactRepo.readAddresses(this.getOrg(), contact.id)
      .pipe(map((w) => w.getDatas()));
  }

  public updateAddress(contact: ContactDto, address: AddressDto): Observable<AddressDto> {
    this.clearCache(contact.id);
    return this.contactRepo.updateAddress(this.getOrg(), contact.id, address)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteAddress(contact: ContactDto, address: AddressDto): Observable<VoidDto> {
    this.clearCache(contact.id);
    return this.contactRepo.deleteAddress(this.getOrg(), contact.id, address.id)
      .pipe(map((w) => w.getFirstData()));
  }

  public getAddressLabels(): Observable<string[]> {
    return this.getFilterValueByField('addresses.label');
  }

  // ##########################################################################
  // #### Phone
  // ##########################################################################

  public getPhones(contact: ContactDto): Observable<PhoneDto[]> {
    return this.contactRepo.readPhones(this.getOrg(), contact.id)
      .pipe(map((w) => w.getDatas()));
  }

  public updatePhone(contact: ContactDto, phone: PhoneDto): Observable<PhoneDto> {
    this.clearCache(contact.id);
    return this.contactRepo.updatePhone(this.getOrg(), contact.id, phone)
      .pipe(map((w) => w.getFirstData()));
  }

  public deletePhone(contact: ContactDto, phone: PhoneDto): Observable<VoidDto> {
    this.clearCache(contact.id);
    return this.contactRepo.deletePhone(this.getOrg(), contact.id, phone.id)
      .pipe(map((w) => w.getFirstData()));
  }

  public getPhoneLabels(): Observable<string[]> {
    return this.getFilterValueByField('phones.label');
  }

  // ##########################################################################
  // #### BankAddress
  // ##########################################################################

  public getBankAddresses(contact: ContactDto): Observable<BankAddressDto[]> {
    return this.contactRepo.readBankAddresses(this.getOrg(), contact.id)
      .pipe(map((w) => w.getDatas()));
  }

  public updateBankAddress(contact: ContactDto, bankAddress: BankAddressDto): Observable<BankAddressDto> {
    this.clearCache(contact.id);
    return this.contactRepo.updateBankAddress(this.getOrg(), contact.id, bankAddress)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteBankAddress(contact: ContactDto, bankAddress: BankAddressDto): Observable<VoidDto> {
    this.clearCache(contact.id);
    return this.contactRepo.deleteBankAddress(this.getOrg(), contact.id, bankAddress.id)
      .pipe(map((w) => w.getFirstData()));
  }

  public getBankAddressLabels(): Observable<string[]> {
    return this.getFilterValueByField('bankAddresses.label');
  }

  // ##########################################################################
  // #### Email
  // ##########################################################################

  public getEmails(contact: ContactDto): Observable<EmailDto[]> {
    return this.contactRepo.getEmails(this.getOrg(), contact.id)
      .pipe(map((w) => w.getDatas()));
  }

  public updateEmail(contact: ContactDto, email: EmailDto): Observable<EmailDto> {
    this.clearCache(contact.id);
    return this.contactRepo.updateEmail(this.getOrg(), contact.id, email)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteEmail(contact: ContactDto, email: EmailDto): Observable<VoidDto> {
    this.clearCache(contact.id);
    return this.contactRepo.deleteEmail(this.getOrg(), contact.id, email.id)
      .pipe(map((w) => w.getFirstData()));
  }

  public getEmailLabels(): Observable<string[]> {
    return this.getFilterValueByField('emails.label');
  }

  // ##########################################################################
  // #### Occurrence
  // ##########################################################################

  public getOccurrences(contact: ContactDto): Observable<OccurrenceDto[]> {
    return this.contactRepo.getOccurrences(this.getOrg(), contact.id)
      .pipe(map((w) => w.getDatas()));
  }

  public updateOccurrence(contact: ContactDto, occurrence: OccurrenceDto): Observable<OccurrenceDto> {
    this.clearCache(contact.id);
    return this.contactRepo.updateOccurrence(this.getOrg(), contact.id, occurrence)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteOccurrence(contact: ContactDto, occurrence: OccurrenceDto): Observable<VoidDto> {
    this.clearCache(contact.id);
    return this.contactRepo.deleteOccurrence(this.getOrg(), contact.id, occurrence.id)
      .pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // #### Hyperlink
  // ##########################################################################

  public getHyperlinks(contact: ContactDto): Observable<HyperlinkDto[]> {
    return this.contactRepo.getHyperlinks(this.getOrg(), contact.id)
      .pipe(map((w) => w.getDatas()));
  }

  public updateHyperlink(contact: ContactDto, hyperlink: HyperlinkDto): Observable<HyperlinkDto> {
    this.clearCache(contact.id);
    return this.contactRepo.updateHyperlink(this.getOrg(), contact.id, hyperlink)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteHyperlink(contact: ContactDto, hyperlink: HyperlinkDto): Observable<VoidDto> {
    this.clearCache(contact.id);
    return this.contactRepo.deleteHyperlink(this.getOrg(), contact.id, hyperlink.id)
      .pipe(map((w) => w.getFirstData()));
  }

  // ##########################################################################
  // #### Typeahead
  // ##########################################################################

  public typeahead(input: string, category?: ContactCategory, type?: ContactType): Observable<SearchContactRes> {
    const request = this.buildTypeaheadRequest(input);
    if (isDefined(category)) {
      request.filters.push(new SearchFilterRequest({
        id: 'category',
        pos: 1001,
        filterValueField: "categories",
        selected: [category]
      }));
    }
    if (isDefined(type)) {
      request.filters.push(new SearchFilterRequest({
        id: 'type',
        pos: 1002,
        filterValueField: "type",
        selected: [type]
      }));
    }
    return this.contactRepo.search(this.getOrg(), request)
      .pipe(map((w) => w.getFirstData()));
  }

  protected override filterValueSearch(request: SearchRequest): Observable<WrapperDto<SearchContactRes>> {
    return this.contactRepo.search(this.getOrg(), request);
  }

  private buildTypeaheadRequest(input: string) {
    return new SearchRequest({
      query: input,
      page: 1,
      pageSize: 100,
      sortField: 'number',
      sortOrder: 'desc',
      fields: ['id', 'type', 'number', 'fullName'],
      filters: [new SearchFilterRequest({pos: 1010, filterValueField: "editable", selected: ['true']})]
    });
  }
}
